import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Mail, Pencil, Plus, Send, ShieldCheck, Smartphone, Trash2, Users, Wifi,
} from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Drawer, ToastProvider, useToast, apiGet, adminMutate, usePermissions } from './primitives.js'
import { Field, TextInput, SwitchRow } from './form.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { useSaveBar } from '../shell/editing.js'
import { navigate } from '../router.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { SondeSmtpCopie } from '../supervision/SondeSmtpCopie.js'

/**
 * PARAMÈTRES → COMMUNICATIONS (LOT 9).
 *
 * Général (état des canaux + politique d'organisation) · E-mail (profils
 * SMTP, secrets AU VAULT, tests honnêtes) · Push (préparé, jamais simulé) ·
 * Modèles · Groupes · Journal. Toute modification persistée passe par la
 * Floating Save Bar — aucun bouton « Enregistrer » isolé.
 */

interface Provider {
  id: string; name: string; host: string; port: number; security: string
  auth_enabled: boolean; username: string; credential_configured: boolean
  from_name: string; from_email: string; reply_to: string | null
  is_enabled: boolean; is_default: boolean
  last_test_at: string | null; last_test_ok: boolean | null; last_test_detail: string | null
}
interface Overview {
  channels: { channel: string; label: string; state: string; detail: string }[]
  week_stats: { channel: string; status: string; count: number }[]
}
interface PolicyItem { category: string; channel: string; mode: string }
interface Group { id: string; name: string; description: string | null; is_enabled: boolean; members: { id: string; name: string }[] }
interface TemplateItem { id: string; key: string | null; name: string; description: string | null; channel: string; event: string; is_system: boolean }
interface JournalItem {
  id: string; channel: string; provider: string; status: string; attempt: number
  latency_ms: number | null; error_class: string | null; error_message: string | null
  skip_reason: string | null; title: string; recipient: string; created_at: string
}

const CH_LABEL: Record<string, string> = { IN_APP: 'In-App', EMAIL: 'E-mail', PUSH: 'Push' }
const CAT_LABEL: Record<string, string> = { CRITICAL: 'Critique', WARNING: 'Attention', INFO: 'Information', RECOVERY: 'Rétablissement' }
const MODE_LABEL: Record<string, string> = { MANDATORY: 'Obligatoire', ALLOWED: 'Autorisé', DISABLED: 'Désactivé' }
const DSTATUS: Record<string, string> = {
  QUEUED: 'En file', PROCESSING: 'En cours', ACCEPTED: 'Accepté', DELIVERED: 'Délivré',
  FAILED: 'Échec', SKIPPED: 'Ignoré', DEFERRED: 'Préparé',
}
const fmtDT = (iso: string | null) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

const TABS = [['general', 'Général'], ['email', 'E-mail'], ['push', 'Push'], ['templates', 'Modèles'], ['groups', 'Groupes'], ['journal', 'Journal']] as const

function Inner() {
  const { permissions, authenticated } = usePermissions()
  const canManage = authenticated && permissions.includes('communication.settings.manage')
  const [tab, setTab] = useState<string>(() => new URLSearchParams(window.location.search).get('tab') ?? 'general')

  useEffect(() => {
    window.history.replaceState(null, '', tab === 'general' ? '/settings/communications' : `/settings/communications?tab=${tab}`)
  }, [tab])

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Communications</h1>
      <p className="lc-subtitle">
        Qui est prévenu, par quel canal, avec quel message — et ce qui a réellement été envoyé.
      </p>

      <div className="lc-seg" role="tablist" aria-label="Sections" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(([v, l]) => (
          <button key={v} role="tab" aria-selected={tab === v}
            className={`lc-seg__btn ${tab === v ? 'lc-seg__btn--on' : ''}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {tab === 'general' ? <GeneralTab canManage={canManage} /> : null}
      {tab === 'email' ? <EmailTab canManage={canManage} /> : null}
      {tab === 'push' ? <PushTab canManage={canManage} /> : null}
      {tab === 'templates' ? <TemplatesTab /> : null}
      {tab === 'groups' ? <GroupsTab canManage={canManage} /> : null}
      {tab === 'journal' ? <JournalTab /> : null}
    </main>
  )
}

/* ═══════════════ GÉNÉRAL — état des canaux + politique ═══════════════ */

function GeneralTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const [ov, setOv] = useState<Overview | null>(null)
  const [policy, setPolicy] = useState<PolicyItem[] | null>(null)
  const [draft, setDraft] = useState<PolicyItem[] | null>(null)

  useEffect(() => {
    apiGet<Overview>('/api/v1/communications/overview').then(setOv).catch(() => undefined)
    apiGet<{ items: PolicyItem[] }>('/api/v1/communications/policy')
      .then((j) => { setPolicy(j.items); setDraft(j.items.map((x) => ({ ...x }))) })
      .catch(() => undefined)
  }, [])

  const dirty = useMemo(() => JSON.stringify(policy) !== JSON.stringify(draft), [policy, draft])
  useSaveBar({
    dirty: dirty && canManage,
    count: draft && policy ? draft.filter((d, i) => d.mode !== policy[i]?.mode).length : 0,
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/communications/policy', { items: draft })
      if (r.status < 300) { setPolicy(draft!.map((x) => ({ ...x }))); return true }
      return r.data.message ?? 'L’enregistrement a échoué.'
    },
    onCancel: () => setDraft(policy?.map((x) => ({ ...x })) ?? null),
    successMessage: 'Politique de notification enregistrée.',
  })

  const stateDot = (s: string) => s === 'OPERATIONAL' ? 'lc-dot--ok lc-dot--live' : s === 'DEGRADED' ? 'lc-dot--warning' : 'lc-dot--idle'

  const testNotif = async (channel: string) => {
    const r = await adminMutate<{ error?: string; ok?: boolean; status?: string; detail?: string; in_app?: number; email_queued?: number; message?: string }>(
      'POST', '/api/v1/communications/test-notification', { channel })
    if (r.status >= 300) { toast('error', r.data.message ?? 'Test impossible.'); return }
    if (r.data.status === 'DEFERRED') toast('error', r.data.detail ?? 'Application mobile non connectée.')
    else toast('ok', channel === 'IN_APP' ? 'Notification de test envoyée — regardez la cloche.' : 'Test envoyé : In-App immédiat, e-mail en file (voir le Journal).')
  }

  return (
    <>
      <section className="lc-panel gd-panel" aria-label="État des canaux">
        <header className="lc-panel__head"><h2 className="lc-section-title">Canaux</h2></header>
        <div className="lc-panel__body lc-panel__body--flush">
          {(ov?.channels ?? []).map((c) => (
            <div key={c.channel} className="lc-action-row" style={{ cursor: 'default' }}>
              <span className="lc-action-row__icon" aria-hidden="true">
                {c.channel === 'EMAIL' ? <Mail size={18} /> : c.channel === 'PUSH' ? <Smartphone size={18} /> : <Wifi size={18} />}
              </span>
              <div className="lc-action-row__body">
                <div className="lc-action-row__title">{c.label}</div>
                <div className="lc-action-row__meta">{c.detail}</div>
              </div>
              <span className={`lc-dot ${stateDot(c.state)}`} aria-hidden="true" />
              {canManage && c.channel !== 'PUSH' ? (
                <button className="lc-btn lc-btn--ghost" onClick={() => testNotif(c.channel)}>
                  <Send size={13} aria-hidden="true" /> Tester
                </button>
              ) : null}
              {c.channel === 'PUSH' && canManage ? (
                <button className="lc-btn lc-btn--ghost" onClick={() => testNotif('PUSH')}>
                  <Send size={13} aria-hidden="true" /> Tester
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="lc-panel gd-panel" aria-label="Politique d’organisation">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><ShieldCheck size={14} aria-hidden="true" /> Politique d’organisation</h2>
        </header>
        <div className="lc-panel__body">
          <p className="lc-hint" style={{ marginBottom: 12 }}>
            Un canal <strong>Obligatoire</strong> ne peut pas être coupé par une préférence personnelle
            (ni par les heures silencieuses). <strong>Autorisé</strong> : chacun choisit.
            <strong> Désactivé</strong> : personne ne le reçoit.
          </p>
          <div className="cm-policy" role="table" aria-label="Politique par catégorie et canal">
            <div className="cm-policy__row cm-policy__row--head" role="row">
              <span role="columnheader">Catégorie</span>
              {['IN_APP', 'EMAIL', 'PUSH'].map((ch) => <span key={ch} role="columnheader">{CH_LABEL[ch]}</span>)}
            </div>
            {['CRITICAL', 'WARNING', 'INFO', 'RECOVERY'].map((cat) => (
              <div key={cat} className="cm-policy__row" role="row">
                <span role="cell"><strong>{CAT_LABEL[cat]}</strong></span>
                {['IN_APP', 'EMAIL', 'PUSH'].map((ch) => {
                  const item = draft?.find((x) => x.category === cat && x.channel === ch)
                  return (
                    <span key={ch} role="cell">
                      <select aria-label={`${CAT_LABEL[cat]} — ${CH_LABEL[ch]}`} disabled={!canManage}
                        value={item?.mode ?? 'ALLOWED'}
                        onChange={(e) => setDraft((d) => d?.map((x) => x.category === cat && x.channel === ch ? { ...x, mode: e.target.value } : x) ?? null)}>
                        {Object.entries(MODE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </span>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

/* ═══════════════ E-MAIL — profils SMTP ═══════════════ */

interface ProviderDraft {
  name: string; host: string; port: number; security: string; auth_enabled: boolean
  username: string; password: string; from_name: string; from_email: string
  reply_to: string; is_enabled: boolean; is_default: boolean
}
const providerDraftOf = (p: Provider | null): ProviderDraft => p ? {
  name: p.name, host: p.host, port: p.port, security: p.security, auth_enabled: p.auth_enabled,
  username: p.username, password: '', from_name: p.from_name, from_email: p.from_email,
  reply_to: p.reply_to ?? '', is_enabled: p.is_enabled, is_default: p.is_default,
} : {
  name: '', host: '', port: 587, security: 'STARTTLS', auth_enabled: true,
  username: '', password: '', from_name: 'Live Club Rennes', from_email: '',
  reply_to: '', is_enabled: true, is_default: true,
}

function EmailTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const [items, setItems] = useState<Provider[] | null>(null)
  const [editing, setEditing] = useState<Provider | null | 'new'>(null)
  const [draft, setDraft] = useState<ProviderDraft | null>(null)
  const [initial, setInitial] = useState<ProviderDraft | null>(null)
  // Sonde recopie le profil actif après chaque enregistrement : le bandeau se relit.
  const [relireSonde, setRelireSonde] = useState(0)

  const load = useCallback(() => {
    apiGet<{ items: Provider[] }>('/api/v1/communications/providers').then((j) => setItems(j.items)).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])

  const open = (p: Provider | 'new') => {
    const d = providerDraftOf(p === 'new' ? null : p)
    setEditing(p); setDraft(d); setInitial(d)
  }

  const dirty = draft !== null && initial !== null && JSON.stringify(draft) !== JSON.stringify(initial)
  useSaveBar({
    dirty: dirty && canManage,
    saveLabel: editing === 'new' ? 'Créer le profil' : 'Enregistrer',
    invalid: draft && (!draft.name || !draft.host || !draft.from_email) ? 'Nom, hôte et adresse expéditeur sont requis.' : null,
    onSave: async () => {
      if (!draft) return 'Rien à enregistrer.'
      const body = { ...draft, password: draft.password || null, reply_to: draft.reply_to || null }
      const r = editing === 'new'
        ? await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/communications/providers', body)
        : await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/communications/providers/${(editing as Provider).id}`, body)
      if (r.status < 300) { setEditing(null); setDraft(null); setInitial(null); load(); setRelireSonde((n) => n + 1); return true }
      return r.data.message ?? 'L’enregistrement a échoué.'
    },
    onCancel: () => { setDraft(initial) },
    successMessage: 'Profil e-mail enregistré. Le mot de passe est au coffre — il ne sera jamais réaffiché.',
  })

  const test = async (p: Provider, kind: 'connection' | 'email') => {
    const r = await adminMutate<{ error?: string; ok?: boolean; detail?: string; message?: string }>(
      'POST', `/api/v1/communications/providers/${p.id}/test-${kind === 'connection' ? 'connection' : 'email'}`,
      kind === 'email' ? {} : undefined)
    if (r.status >= 300) { toast('error', r.data.message ?? 'Test impossible.'); return }
    if (r.data.ok) toast('ok', kind === 'connection' ? 'Connexion SMTP réussie.' : `E-mail de test accepté par le serveur SMTP (${r.data.detail ?? ''}).`)
    else toast('error', r.data.detail ?? 'Échec du test.')
    load()
  }

  return (
    <>
      <SondeSmtpCopie variante="bandeau" relire={relireSonde} />
      <section className="lc-panel gd-panel" aria-label="Profils e-mail">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Profils d’expéditeur</h2>
          {canManage ? (
            <button className="lc-btn lc-btn--ghost" onClick={() => open('new')}>
              <Plus size={14} aria-hidden="true" /> Nouveau profil
            </button>
          ) : null}
        </header>
        <div className="lc-panel__body lc-panel__body--flush">
          {items?.length === 0 ? (
            <p className="lc-hint" style={{ padding: 'var(--sp-4)' }}>
              Aucun profil SMTP. Créez-en un pour que Gestion puisse envoyer des e-mails —
              le mot de passe rejoint le coffre chiffré, jamais les réglages en clair.
            </p>
          ) : null}
          {items?.map((p) => (
            <div key={p.id} className="lc-action-row" style={{ cursor: 'default' }}>
              <span className="lc-action-row__icon" aria-hidden="true"><Mail size={18} /></span>
              <div className="lc-action-row__body">
                <div className="lc-action-row__title">
                  {p.name}
                  {p.is_default ? <span className="lc-pill lc-pill--ok" style={{ marginLeft: 8 }}>Par défaut</span> : null}
                  {!p.is_enabled ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Désactivé</span> : null}
                </div>
                <div className="lc-action-row__meta">
                  {p.host}:{p.port} · {p.security} · {p.from_email}
                  · mot de passe {p.credential_configured ? 'configuré' : 'NON configuré'}
                  {p.last_test_at ? ` · dernier test ${fmtDT(p.last_test_at)} : ${p.last_test_ok ? 'réussi' : 'échec'}` : ' · jamais testé'}
                </div>
              </div>
              {canManage ? (
                <span style={{ display: 'flex', gap: 6 }}>
                  <button className="lc-btn lc-btn--ghost" onClick={() => test(p, 'connection')}>Tester la connexion</button>
                  <button className="lc-btn lc-btn--ghost" onClick={() => test(p, 'email')}>
                    <Send size={13} aria-hidden="true" /> E-mail de test
                  </button>
                  <button className="lc-btn lc-btn--ghost" aria-label="Modifier" onClick={() => open(p)}><Pencil size={13} aria-hidden="true" /></button>
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <Drawer open={editing !== null} title={editing === 'new' ? 'Nouveau profil e-mail' : `Profil « ${(editing as Provider | null)?.name ?? ''} »`}
        onClose={() => { setEditing(null); setDraft(null); setInitial(null) }}>
        {draft ? (
          <div className="ad-catalog__form">
            <label className="adm-field"><span>Nom</span>
              <input value={draft.name} placeholder="SMTP principal" onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <div className="rb-cond">
              <label className="adm-field"><span>Hôte</span>
                <input value={draft.host} placeholder="smtp.example.com" onChange={(e) => setDraft({ ...draft, host: e.target.value })} /></label>
              <label className="adm-field"><span>Port</span>
                <input type="number" value={draft.port} onChange={(e) => setDraft({ ...draft, port: Number(e.target.value) || 587 })} /></label>
            </div>
            <label className="adm-field"><span>Sécurité</span>
              <select value={draft.security} onChange={(e) => setDraft({ ...draft, security: e.target.value })}>
                <option value="STARTTLS">STARTTLS (587)</option>
                <option value="TLS">TLS implicite (465)</option>
                <option value="NONE">Aucune (déconseillé)</option>
              </select></label>
            <label className="ad-toggle">
              <input type="checkbox" checked={draft.auth_enabled} onChange={(e) => setDraft({ ...draft, auth_enabled: e.target.checked })} />
              <span className="lc-stack"><span>Authentification</span></span>
            </label>
            {draft.auth_enabled ? (
              <>
                <label className="adm-field"><span>Utilisateur</span>
                  <input value={draft.username} autoComplete="off" onChange={(e) => setDraft({ ...draft, username: e.target.value })} /></label>
                <label className="adm-field">
                  <span>Mot de passe {editing !== 'new' && (editing as Provider).credential_configured ? '(configuré — laisser vide pour conserver)' : ''}</span>
                  <input type="password" value={draft.password} autoComplete="new-password"
                    placeholder={editing !== 'new' && (editing as Provider).credential_configured ? '••••••••' : ''}
                    onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
                  <span className="lc-hint">Stocké au coffre chiffré de Gestion. Jamais réaffiché, jamais journalisé.</span>
                </label>
              </>
            ) : null}
            <div className="rb-cond">
              <label className="adm-field"><span>Nom d’expéditeur</span>
                <input value={draft.from_name} onChange={(e) => setDraft({ ...draft, from_name: e.target.value })} /></label>
              <label className="adm-field"><span>Adresse d’expéditeur</span>
                <input value={draft.from_email} placeholder="gestion@liveclubrennes.com" onChange={(e) => setDraft({ ...draft, from_email: e.target.value })} /></label>
            </div>
            <label className="adm-field"><span>Reply-To (facultatif)</span>
              <input value={draft.reply_to} onChange={(e) => setDraft({ ...draft, reply_to: e.target.value })} /></label>
            <label className="ad-toggle">
              <input type="checkbox" checked={draft.is_enabled} onChange={(e) => setDraft({ ...draft, is_enabled: e.target.checked })} />
              <span className="lc-stack"><span>Profil actif</span></span>
            </label>
            <label className="ad-toggle">
              <input type="checkbox" checked={draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} />
              <span className="lc-stack"><span>Expéditeur par défaut</span>
                <span className="lc-hint">Plusieurs profils possibles (Gestion, RH, Technique…) — un seul par défaut.</span></span>
            </label>
          </div>
        ) : null}
      </Drawer>
    </>
  )
}

/* ═══════════════ PUSH — transports RÉELS APNs / FCM (LOT 9.1) ═══════════════ */

interface PushConfig {
  apns: { state: string; team_id: string; key_id: string; bundle_id: string; sandbox: boolean; key_configured: boolean }
  fcm: { state: string; project_id: string; client_email: string; key_configured: boolean }
  devices_registered: number
}

function PushTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const [cfg, setCfg] = useState<PushConfig | null>(null)
  const [apns, setApns] = useState({ team_id: '', key_id: '', bundle_id: 'com.liveclubrennes.gestion', sandbox: false, private_key: '' })
  const [fcmJson, setFcmJson] = useState('')
  const [initial, setInitial] = useState('')

  const load = useCallback(() => {
    apiGet<PushConfig>('/api/v1/communications/push-config').then((j) => {
      setCfg(j)
      const a = { team_id: j.apns.team_id, key_id: j.apns.key_id, bundle_id: j.apns.bundle_id || 'com.liveclubrennes.gestion', sandbox: j.apns.sandbox, private_key: '' }
      setApns(a); setFcmJson(''); setInitial(JSON.stringify([a, '']))
    }).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])

  const dirty = JSON.stringify([apns, fcmJson]) !== initial
  useSaveBar({
    dirty: dirty && canManage,
    onSave: async () => {
      if (JSON.stringify(apns) !== JSON.parse(initial || '[{}]')[0] || apns.private_key) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/communications/push-config/apns', {
          team_id: apns.team_id, key_id: apns.key_id, bundle_id: apns.bundle_id,
          sandbox: apns.sandbox, private_key: apns.private_key || null,
        })
        if (r.status >= 300) return r.data.message ?? 'Enregistrement APNs refusé.'
      }
      if (fcmJson.trim()) {
        const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/communications/push-config/fcm', { service_account_json: fcmJson })
        if (r.status >= 300) return r.data.message ?? 'Enregistrement FCM refusé.'
      }
      load()
      return true
    },
    onCancel: () => load(),
    successMessage: 'Configuration Push enregistrée — secrets au coffre, jamais réaffichés.',
  })

  const pushTest = async () => {
    const r = await adminMutate<{ error?: string; ok?: boolean; detail?: string; message?: string }>('POST', '/api/v1/communications/push-test')
    if (r.status >= 300) toast('error', r.data.message ?? 'Test impossible.')
    else toast(r.data.ok ? 'ok' : 'error', r.data.detail ?? '')
  }

  const stateDot = (s?: string) => s === 'READY' ? 'lc-dot--ok lc-dot--live' : 'lc-dot--idle'

  return (
    <>
      <section className="lc-panel gd-panel" aria-label="État Push">
        <header className="lc-panel__head"><h2 className="lc-section-title"><Smartphone size={14} aria-hidden="true" /> Transports Push</h2></header>
        <div className="lc-panel__body">
          <p className="lc-hint" style={{ marginBottom: 10 }}>
            Envois DIRECTS : APNs (Apple) et FCM HTTP v1 (Google) — aucun intermédiaire tiers.
            Les clés privées vont au coffre chiffré de Gestion : jamais réaffichées, jamais
            dans l’application, jamais dans les journaux.
            Appareils enregistrés : <strong>{cfg?.devices_registered ?? '…'}</strong>.
          </p>
          <div className="rb-cond">
            <div className="rw-comm__card">
              <span className="rw-comm__head"><span className={`lc-dot ${stateDot(cfg?.apns.state)}`} aria-hidden="true" /> iOS · APNs — {cfg?.apns.state === 'READY' ? 'Prêt' : 'Non configuré'}</span>
              <Field label="Team ID"><TextInput value={apns.team_id} onChange={(v) => setApns({ ...apns, team_id: v })} placeholder="A1B2C3D4E5" /></Field>
              <Field label="Key ID"><TextInput value={apns.key_id} onChange={(v) => setApns({ ...apns, key_id: v })} placeholder="ABC123DEFG" /></Field>
              <Field label="Bundle ID"><TextInput value={apns.bundle_id} onChange={(v) => setApns({ ...apns, bundle_id: v })} /></Field>
              <Field label={`Clé privée .p8 ${cfg?.apns.key_configured ? '(configurée — laisser vide pour conserver)' : ''}`}
                helper="Apple Push Notification Authentication Key — stockée au coffre.">
                <textarea className="lc-input" style={{ height: 90, padding: 10, fontFamily: 'var(--mono)', fontSize: 11 }}
                  value={apns.private_key} placeholder="-----BEGIN PRIVATE KEY-----"
                  onChange={(e) => setApns({ ...apns, private_key: e.target.value })} />
              </Field>
              <SwitchRow checked={apns.sandbox} onChange={(v) => setApns({ ...apns, sandbox: v })}
                title="Environnement sandbox" desc="Builds de développement (api.sandbox.push.apple.com)." />
            </div>
            <div className="rw-comm__card">
              <span className="rw-comm__head"><span className={`lc-dot ${stateDot(cfg?.fcm.state)}`} aria-hidden="true" /> Android · FCM — {cfg?.fcm.state === 'READY' ? 'Prêt' : 'Non configuré'}</span>
              {cfg?.fcm.state === 'READY' ? (
                <dl className="lc-kv">
                  <dt>Projet</dt><dd>{cfg.fcm.project_id}</dd>
                  <dt>Service account</dt><dd>{cfg.fcm.client_email}</dd>
                  <dt>Clé</dt><dd>Configurée (au coffre)</dd>
                </dl>
              ) : null}
              <Field label={`Service account JSON ${cfg?.fcm.key_configured ? '(configuré — coller pour remplacer)' : ''}`}
                helper="Fichier de compte de service Firebase (FCM HTTP v1) — la clé privée part au coffre.">
                <textarea className="lc-input" style={{ height: 150, padding: 10, fontFamily: 'var(--mono)', fontSize: 11 }}
                  value={fcmJson} placeholder='{ "type": "service_account", … }'
                  onChange={(e) => setFcmJson(e.target.value)} />
              </Field>
            </div>
          </div>
          {canManage ? (
            <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="lc-btn" onClick={pushTest}><Send size={14} aria-hidden="true" /> Envoyer un Push de test (vers mes appareils)</button>
              <span className="lc-hint">Résultat honnête au Journal : « Accepté par APNs/FCM », jamais « reçu ».</span>
            </div>
          ) : null}
        </div>
      </section>
    </>
  )
}

/* ═══════════════ MODÈLES ═══════════════ */

function TemplatesTab() {
  const [items, setItems] = useState<TemplateItem[] | null>(null)
  useEffect(() => {
    apiGet<{ items: TemplateItem[] }>('/api/v1/communications/templates').then((j) => setItems(j.items)).catch(() => undefined)
  }, [])
  const byChannel: { ch: string; list: TemplateItem[] }[] = []
  for (const t of items ?? []) {
    const last = byChannel.find((x) => x.ch === t.channel)
    if (last) last.list.push(t)
    else byChannel.push({ ch: t.channel, list: [t] })
  }
  const EVENT_LABEL: Record<string, string> = { INCIDENT: 'Incident', RECOVERY: 'Rétablissement', TEST: 'Test', GENERIC: 'Générique' }
  return (
    <>
      {byChannel.map((g) => (
        <section key={g.ch} className="lc-panel gd-panel" aria-label={CH_LABEL[g.ch]}>
          <header className="lc-panel__head"><h2 className="lc-section-title">{CH_LABEL[g.ch]}</h2></header>
          <div className="lc-panel__body lc-panel__body--flush">
            {g.list.map((t) => (
              <div key={t.id} className="lc-action-row" role="button" tabIndex={0}
                onClick={() => navigate(`/settings/communications/templates/${t.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/settings/communications/templates/${t.id}`) }}>
                <div className="lc-action-row__body">
                  <div className="lc-action-row__title">
                    {t.name}
                    {t.is_system ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Système</span> : null}
                  </div>
                  <div className="lc-action-row__meta">{EVENT_LABEL[t.event] ?? t.event}{t.description ? ` · ${t.description}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  )
}

/* ═══════════════ GROUPES ═══════════════ */

function GroupsTab({ canManage }: { canManage: boolean }) {
  const toast = useToast()
  const [items, setItems] = useState<Group[] | null>(null)
  const [users, setUsers] = useState<{ id: string; display_name: string }[]>([])
  const [editing, setEditing] = useState<Group | null | 'new'>(null)
  const [draft, setDraft] = useState<{ name: string; description: string; member_ids: string[] } | null>(null)
  const [initial, setInitial] = useState<string>('')
  const [toDelete, setToDelete] = useState<Group | null>(null)

  const load = useCallback(() => {
    apiGet<{ items: Group[] }>('/api/v1/communications/groups').then((j) => setItems(j.items)).catch(() => undefined)
    apiGet<{ recipients: { users: { id: string; display_name: string }[] } }>('/api/v1/alert-rules/options')
      .then((j) => setUsers(j.recipients.users)).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])

  const open = (g: Group | 'new') => {
    const d = g === 'new'
      ? { name: '', description: '', member_ids: [] }
      : { name: g.name, description: g.description ?? '', member_ids: g.members.map((m) => m.id) }
    setEditing(g); setDraft(d); setInitial(JSON.stringify(d))
  }

  useSaveBar({
    dirty: draft !== null && JSON.stringify(draft) !== initial && canManage,
    saveLabel: editing === 'new' ? 'Créer le groupe' : 'Enregistrer',
    invalid: draft && !draft.name.trim() ? 'Le groupe a besoin d’un nom.' : null,
    onSave: async () => {
      if (!draft) return 'Rien à enregistrer.'
      const body = { name: draft.name.trim(), description: draft.description.trim() || null, is_enabled: true, member_ids: draft.member_ids }
      const r = editing === 'new'
        ? await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/communications/groups', body)
        : await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/communications/groups/${(editing as Group).id}`, body)
      if (r.status < 300) { setEditing(null); setDraft(null); load(); return true }
      return r.data.message ?? 'L’enregistrement a échoué.'
    },
    onCancel: () => { setDraft(JSON.parse(initial || 'null')) },
    successMessage: 'Groupe enregistré — les règles qui le ciblent suivent automatiquement.',
  })

  const doDelete = async () => {
    if (!toDelete) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/communications/groups/${toDelete.id}`)
    if (r.status < 300) { toast('ok', 'Groupe supprimé.'); setToDelete(null); load() }
    else toast('error', r.data.message ?? 'La suppression a échoué.')
  }

  return (
    <>
      <section className="lc-panel gd-panel" aria-label="Groupes de notification">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Users size={14} aria-hidden="true" /> Groupes de notification</h2>
          {canManage ? (
            <button className="lc-btn lc-btn--ghost" onClick={() => open('new')}>
              <Plus size={14} aria-hidden="true" /> Nouveau groupe
            </button>
          ) : null}
        </header>
        <div className="lc-panel__body lc-panel__body--flush">
          {items?.length === 0 ? (
            <p className="lc-hint" style={{ padding: 'var(--sp-4)' }}>
              Aucun groupe. Un groupe (ex. « Astreinte Technique ») est une liste de personnes —
              indépendante des rôles et permissions. Les règles le ciblent ; ses membres évoluent librement.
            </p>
          ) : null}
          {items?.map((g) => (
            <div key={g.id} className="lc-action-row" style={{ cursor: 'default' }}>
              <div className="lc-action-row__body">
                <div className="lc-action-row__title">{g.name}</div>
                <div className="lc-action-row__meta">
                  {g.members.length === 0 ? 'Aucun membre' : g.members.map((m) => m.name).join(', ')}
                  {g.description ? ` · ${g.description}` : ''}
                </div>
              </div>
              {canManage ? (
                <span style={{ display: 'flex', gap: 6 }}>
                  <button className="lc-btn lc-btn--ghost" aria-label="Modifier" onClick={() => open(g)}><Pencil size={13} aria-hidden="true" /></button>
                  <button className="lc-btn lc-btn--ghost" aria-label="Supprimer" onClick={() => setToDelete(g)}><Trash2 size={13} aria-hidden="true" /></button>
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <Drawer open={editing !== null} title={editing === 'new' ? 'Nouveau groupe' : `Groupe « ${(editing as Group | null)?.name ?? ''} »`}
        onClose={() => { setEditing(null); setDraft(null) }}>
        {draft ? (
          <div className="ad-catalog__form">
            <label className="adm-field"><span>Nom</span>
              <input value={draft.name} placeholder="Astreinte Technique" onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <label className="adm-field"><span>Description</span>
              <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
            <fieldset className="adm-field">
              <span>Membres</span>
              <div className="rb-chips">
                {users.map((u) => {
                  const on = draft.member_ids.includes(u.id)
                  return (
                    <button key={u.id} className={`rb-chip ${on ? 'rb-chip--on' : ''}`} aria-pressed={on}
                      onClick={() => setDraft({ ...draft, member_ids: on ? draft.member_ids.filter((x) => x !== u.id) : [...draft.member_ids, u.id] })}>
                      {u.display_name}
                    </button>
                  )
                })}
              </div>
            </fieldset>
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog open={toDelete !== null} danger title="Supprimer ce groupe ?" confirmLabel="Supprimer"
        onConfirm={() => void doDelete()} onClose={() => setToDelete(null)}>
        Les règles qui ciblent « {toDelete?.name} » ne préviendront plus personne par ce groupe.
      </ConfirmDialog>
    </>
  )
}

/* ═══════════════ JOURNAL ═══════════════ */

function JournalTab() {
  const [items, setItems] = useState<JournalItem[] | null>(null)
  const [filter, setFilter] = useState<{ channel: string; status: string }>({ channel: '', status: '' })
  useEffect(() => {
    const p = new URLSearchParams({ limit: '100' })
    if (filter.channel) p.set('channel', filter.channel)
    if (filter.status) p.set('status', filter.status)
    apiGet<{ items: JournalItem[] }>(`/api/v1/communications/journal?${p}`).then((j) => setItems(j.items)).catch(() => undefined)
  }, [filter])

  const pill = (s: string) => s === 'ACCEPTED' || s === 'DELIVERED' ? 'lc-pill--ok' : s === 'FAILED' ? 'lc-pill--danger' : 'lc-pill--idle'

  return (
    <section className="lc-panel gd-panel" aria-label="Journal des envois">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">Journal des envois</h2>
        <span style={{ display: 'flex', gap: 8 }}>
          <select aria-label="Canal" value={filter.channel} onChange={(e) => setFilter((f) => ({ ...f, channel: e.target.value }))}>
            <option value="">Tous canaux</option>
            {Object.entries(CH_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select aria-label="Statut" value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Tous statuts</option>
            {Object.entries(DSTATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </span>
      </header>
      <div className="lc-panel__body lc-panel__body--flush">
        {items?.length === 0 ? <p className="lc-hint" style={{ padding: 'var(--sp-4)' }}>Aucune tentative d’envoi pour ces filtres.</p> : null}
        {items?.map((d) => (
          <div key={d.id} className="lc-action-row cm-journal-row" style={{ cursor: 'default' }}>
            <div className="lc-action-row__body">
              <div className="lc-action-row__title">{d.title}</div>
              <div className="lc-action-row__meta">
                {CH_LABEL[d.channel] ?? d.channel} → {d.recipient}
                {d.provider ? ` · via ${d.provider}` : ''}
                {d.attempt > 1 ? ` · tentative ${d.attempt}` : ''}
                {d.latency_ms !== null ? ` · ${d.latency_ms} ms` : ''}
                {d.error_class ? ` · ${d.error_class}` : ''}
                {d.skip_reason ? ` · ${d.skip_reason}` : ''}
              </div>
            </div>
            <span className={`lc-pill ${pill(d.status)}`}>{DSTATUS[d.status] ?? d.status}</span>
            <span className="ad-alert-row__time">{fmtDT(d.created_at)}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

export function CommunicationsPage() {
  const meta = useShellMeta()
  return (
    <ToastProvider>
      <Shell active="settings" meta={meta}>
        <Inner />
      </Shell>
    </ToastProvider>
  )
}
